import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import { Card } from 'tea-component';
import Chart from '@/pages/dashboard/components/Chart';
import ChartErrorBoundary from '../ChartPreview/ChartErrorBoundary';
import './index.scss';

/**
 * 原生 ECharts options 扩展配置
 * 业务在 JSON 中可通过顶层 height/width/color 控制容器与色板
 */
type TEchartsPreviewOption = EChartsOption & {
  height?: number | string;
  width?: number | string;
  color?: string[];
};

interface IEchartsOptionPreviewProps {
  /** 原生 ECharts options 对象（已通过校验） */
  options: TEchartsPreviewOption;
}

/** 默认容器高度（px） */
const DEFAULT_CHART_HEIGHT = 400;
/** 默认容器宽度 */
const DEFAULT_CHART_WIDTH: number | string = '100%';

/**
 * 原生 ECharts options 渲染预览
 * @description
 * - 直接通过底层 Chart 组件（echarts.setOption + notMerge=true）渲染原生 options
 * - 不使用 AgentEchart：因为 AgentEchart 会强制把 color 注入到 options 中，
 *   会覆盖用户原生 options 的默认配色，破坏「所见即所得」的预览语义
 * - height/width/color 是预览工具自定义的容器/色板控制字段，从 options 中剥离后单独使用
 */
const EchartsOptionPreview: React.FC<IEchartsOptionPreviewProps> = ({ options }) => {
  const { height, width, finalOptions } = useMemo(() => {
    const safeOptions = (options || {}) as TEchartsPreviewOption;
    const { height: h, width: w, color: c, ...rest } = safeOptions;

    // 仅当用户显式提供 color 时才注入；否则保留 echarts 的默认配色
    const merged: EChartsOption = c !== undefined
      ? { ...(rest as EChartsOption), color: c }
      : (rest as EChartsOption);

    return {
      height: h ?? DEFAULT_CHART_HEIGHT,
      width: w ?? DEFAULT_CHART_WIDTH,
      finalOptions: merged,
    };
  }, [options]);

  // 容器 inline style：把 height/width 应用到外层 div，避免依赖具体图表组件支持
  const containerStyle = useMemo<React.CSSProperties>(() => ({
    width,
    height,
  }), [width, height]);

  return (
    <Card className="dba-echarts-option-preview-container">
      <Card.Header>
        <h3>原生图表预览</h3>
      </Card.Header>
      <Card.Body>
        <ChartErrorBoundary>
          <div className="dba-echarts-option-content" style={containerStyle}>
            <Chart option={finalOptions} notMerge />
          </div>
        </ChartErrorBoundary>
      </Card.Body>
    </Card>
  );
};

EchartsOptionPreview.displayName = 'EchartsOptionPreview';

export default EchartsOptionPreview;
